import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { Text } from '@/nova/components';
import { ANIM, COLORS, SPACE, STATUS_COLORS } from '@/nova/theme/tokens';

/** Vertical stack under the finished asset: the trigger or the link, where it goes, and its notices. */
export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  margin-top: ${SPACE[12]};
`;

/** Small caption above the copy field. */
export const FieldLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

/** The tracking control + Download button in one row: the tracking element grows, Download is a fixed square
 *  that stretches to match the row height. */
export const ActionRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${SPACE[8]};

  & > *:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

/** Primary icon + label button beside the tracking control; downloads the finished mp4. Filled CTA style
 *  (orange) so downloading the ad is the prominent action; stretches to the row height (44px field / 48px
 *  Generate button) via min-height + the row's align-stretch. */
export const DownloadBtn = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  min-height: 44px;
  padding: 0 ${SPACE[16]};
  border: 1px solid ${COLORS.ctaSolid};
  border-radius: 99px;
  background: ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};
  cursor: pointer;
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    opacity: 0.9;
  }
`;

export const DownloadBtnText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

/** Subtle placeholder shown in the tracking row while an existing link is being resolved on mount. */
export const LinkSkeleton = styled.div`
  height: 44px;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
`;

/** A premium "read-only URL + contained copy button" control: the link fills the field, the copy action
 *  sits inside on the right behind a hairline divider. */
export const CopyField = styled.div<{ $flash?: boolean }>`
  display: flex;
  align-items: stretch;
  /* Lit for a moment right after the link was created or moved (the confirmation under it says so). */
  border: 1px solid ${(p) => (p.$flash ? COLORS.accent : COLORS.hairline)};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  overflow: hidden;

  &:focus-within {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

/** The URL itself, a read-only input so it selects natively and ellipsises when long. */
export const CopyUrl = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  border: 0;
  background: transparent;
  color: ${COLORS.text};
  font-size: 13px;
  padding: 0 ${SPACE[12]};
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }
`;

/** The contained copy button on the right (icon only), divided from the field by a hairline. */
export const CopyBtn = styled.button<{ $copied: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${SPACE[12]};
  border: 0;
  border-left: 1px solid ${COLORS.hairline};
  cursor: pointer;
  background: transparent;
  color: ${({ $copied }) => ($copied ? STATUS_COLORS.positive : COLORS.textMuted)};
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.hoverWash};
    color: ${({ $copied }) => ($copied ? STATUS_COLORS.positive : COLORS.text)};
  }
`;

/** A quick landing page being built: the Lumio mark, then what is happening and how long it takes. */
export const BuildRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[12]};
  border: 1px solid ${COLORS.hairline};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
`;

export const BuildCopy = styled.div`
  display: flex;
  flex: 1 1 200px;
  flex-direction: column;
  gap: ${SPACE[2]};
  min-width: 0;
`;

/** The page being built, opened in its composer (to watch it, then come back): at the end of the row. */
export const BuildOpen = styled(Link)`
  flex-shrink: 0;
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.accent};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

export const BuildTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const BuildHint = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/** "Goes to: <casino | page name · host/path>" under the link, with "Change" right after it (wrapping onto the next line
 *  on a narrow pane). */
export const GoesTo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${SPACE[4]} ${SPACE[8]};
  min-width: 0;
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

export const GoesToLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-weight: 600;
`;

/** The destination itself (the casino, or a page Studio did not make: its address). Data, ellipsised when long. */
export const GoesToValue = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  color: ${COLORS.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** What follows the destination (its address, or "being designed"), after a middle dot. */
export const GoesToMeta = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::before {
    content: '·';
    margin-right: ${SPACE[8]};
  }
`;

/** A Studio page's name: opens its composer in the same tab. */
export const GoesToPage = styled(Link)`
  min-width: 0;
  color: ${COLORS.text};
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: ${COLORS.hairline};
  text-underline-offset: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: text-decoration-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    text-decoration-color: ${COLORS.text};
  }
`;

/** "Open the landing page" (a page still being designed): a quiet text link. */
export const GoesToAction = styled(Link)`
  color: ${COLORS.accent};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/** "Change": right after where the link goes, on the same line (a text action, underlined like the page's name). */
export const ChangeBtn = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${COLORS.text};
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: ${COLORS.hairline};
  text-underline-offset: 3px;
  cursor: pointer;
  transition: text-decoration-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    text-decoration-color: ${COLORS.text};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const savedIn = keyframes`
  from { opacity: 0; transform: translateY(-${SPACE[4]}); }
  to { opacity: 1; transform: translateY(0); }
`;

/** "Tracking link created…" / "Done. The link now sends people to…": the quiet confirmation under the link. */
export const SavedNote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  font-size: 13px;
  color: ${STATUS_COLORS.positive};
  animation: ${savedIn} ${ANIM.INTERACT_MS}ms ${ANIM.EASE} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
